/**
 * @license
 * Copyright (c) 2021 Handsoncode. All rights reserved.
 */
import { CellError, ErrorType } from '../../Cell';
import { ErrorMessage } from '../../error-message';
import { Condition, CriterionFunctionCompute } from '../CriterionFunctionCompute';
import { getRawValue, isExtendedNumber } from '../InterpreterValue';
import { ArgumentTypes, FunctionPlugin } from './FunctionPlugin';
class AverageResult {
    constructor(sum, count) {
        this.sum = sum;
        this.count = count;
    }
    static single(arg) {
        return new AverageResult(arg, 1);
    }
    compose(other) {
        return new AverageResult(this.sum + other.sum, this.count + other.count);
    }
    averageValue() {
        if (this.count > 0) {
            return this.sum / this.count;
        }
        else {
            return undefined;
        }
    }
}
AverageResult.empty = new AverageResult(0, 0);
/** Computes key for criterion function cache */
function sumifCacheKey(conditions) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const conditionsStrings = conditions.map((c) => `${c.conditionRange.range().sheet},${c.conditionRange.range().start.col},${c.conditionRange.range().start.row}`);
    return ['SUMIF', ...conditionsStrings].join(',');
}
function averageifCacheKey(conditions) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const conditionsStrings = conditions.map((c) => `${c.conditionRange.range().sheet},${c.conditionRange.range().start.col},${c.conditionRange.range().start.row}`);
    return ['AVERAGEIF', ...conditionsStrings].join(',');
}
function countifsCacheKey(conditions) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const conditionsStrings = conditions.map((c) => `${c.conditionRange.range().sheet},${c.conditionRange.range().start.col},${c.conditionRange.range().start.row}`);
    return ['COUNTIFS', ...conditionsStrings].join(',');
}
export class SumifPlugin extends FunctionPlugin {
    /**
     * Corresponds to SUMIF(Range, Criterion, SumRange)
     *
     * Range is the range to which criterion is to be applied.
     * Criterion is the criteria used to choose which cells will be included in sum.
     * SumRange is the range on which adding will be performed.
     *
     * @param ast
     * @param formulaAddress
     */
    sumif(ast, formulaAddress) {
        return this.runFunction(ast.args, formulaAddress, this.metadata('SUMIF'), (conditionArg, criterionValue, valuesArg) => {
            const criterion = this.interpreter.criterionBuilder.fromCellValue(criterionValue, this.interpreter.arithmeticHelper);
            if (criterion === undefined) {
                return new CellError(ErrorType.VALUE, ErrorMessage.BadCriterion);
            }
            valuesArg = valuesArg !== null && valuesArg !== void 0 ? valuesArg : conditionArg;
            return new CriterionFunctionCompute(this.interpreter, sumifCacheKey, 0, (left, right) => this.interpreter.arithmeticHelper.nonstrictadd(left, right), (arg) => getRawValue(arg)).compute(valuesArg, [new Condition(conditionArg, criterion)]);
        });
    }
    sumifs(ast, formulaAddress) {
        return this.runFunction(ast.args, formulaAddress, this.metadata('SUMIFS'), (values, ...args) => {
            const conditions = [];
            for (let i = 0; i < args.length; i += 2) {
                const conditionArg = args[i];
                const criterionPackage = this.interpreter.criterionBuilder.fromCellValue(args[i + 1], this.interpreter.arithmeticHelper);
                if (criterionPackage === undefined) {
                    return new CellError(ErrorType.VALUE, ErrorMessage.BadCriterion);
                }
                conditions.push(new Condition(conditionArg, criterionPackage));
            }
            return new CriterionFunctionCompute(this.interpreter, sumifCacheKey, 0, (left, right) => this.interpreter.arithmeticHelper.nonstrictadd(left, right), (arg) => getRawValue(arg)).compute(values, conditions);
        });
    }
    averageif(ast, formulaAddress) {
        return this.runFunction(ast.args, formulaAddress, this.metadata('AVERAGEIF'), (conditionArg, criterionValue, valuesArg) => {
            const criterion = this.interpreter.criterionBuilder.fromCellValue(criterionValue, this.interpreter.arithmeticHelper);
            if (criterion === undefined) {
                return new CellError(ErrorType.VALUE, ErrorMessage.BadCriterion);
            }
            valuesArg = valuesArg !== null && valuesArg !== void 0 ? valuesArg : conditionArg;
            const averageResult = new CriterionFunctionCompute(this.interpreter, averageifCacheKey, AverageResult.empty, (left, right) => left.compose(right), (arg) => {
                if (isExtendedNumber(arg)) {
                    return AverageResult.single(getRawValue(arg));
                }
                else {
                    return AverageResult.empty;
                }
            }).compute(valuesArg, [new Condition(conditionArg, criterion)]);
            if (averageResult instanceof CellError) {
                return averageResult;
            }
            else {
                return averageResult.averageValue() || new CellError(ErrorType.DIV_BY_ZERO);
            }
        });
    }
    /**
     * Corresponds to COUNTIF(Range, Criterion)
     *
     * Range is the range to which criterion is to be applied.
     * Criterion is the criteria used to choose which cells will be included in sum.
     *
     * Returns number of cells on which criteria evaluates to true.
     *
     * @param ast
     * @param formulaAddress
     */
    countif(ast, formulaAddress) {
        return this.runFunction(ast.args, formulaAddress, this.metadata('COUNTIF'), (conditionArg, criterionValue) => {
            const criterion = this.interpreter.criterionBuilder.fromCellValue(criterionValue, this.interpreter.arithmeticHelper);
            if (criterion === undefined) {
                return new CellError(ErrorType.VALUE, ErrorMessage.BadCriterion);
            }
            return new CriterionFunctionCompute(this.interpreter, () => 'COUNTIF', 0, (left, right) => left + right, () => 1).compute(conditionArg, [new Condition(conditionArg, criterion)]);
        });
    }
    countifs(ast, formulaAddress) {
        return this.runFunction(ast.args, formulaAddress, this.metadata('COUNTIFS'), (...args) => {
            const conditions = [];
            for (let i = 0; i < args.length; i += 2) {
                const conditionArg = args[i];
                const criterionPackage = this.interpreter.criterionBuilder.fromCellValue(args[i + 1], this.interpreter.arithmeticHelper);
                if (criterionPackage === undefined) {
                    return new CellError(ErrorType.VALUE, ErrorMessage.BadCriterion);
                }
                conditions.push(new Condition(conditionArg, criterionPackage));
            }
            return new CriterionFunctionCompute(this.interpreter, countifsCacheKey, 0, (left, right) => left + right, () => 1).compute(conditions[0].conditionRange, conditions);
        });
    }
}
SumifPlugin.implementedFunctions = {
    'SUMIF': {
        method: 'sumif',
        parameters: [
            { argumentType: ArgumentTypes.RANGE },
            { argumentType: ArgumentTypes.NOERROR },
            { argumentType: ArgumentTypes.RANGE, optionalArg: true },
        ],
    },
    'COUNTIF': {
        method: 'countif',
        parameters: [
            { argumentType: ArgumentTypes.RANGE },
            { argumentType: ArgumentTypes.NOERROR },
        ],
    },
    'AVERAGEIF': {
        method: 'averageif',
        parameters: [
            { argumentType: ArgumentTypes.RANGE },
            { argumentType: ArgumentTypes.NOERROR },
            { argumentType: ArgumentTypes.RANGE, optionalArg: true },
        ],
    },
    'SUMIFS': {
        method: 'sumifs',
        parameters: [
            { argumentType: ArgumentTypes.RANGE },
            { argumentType: ArgumentTypes.RANGE },
            { argumentType: ArgumentTypes.NOERROR },
        ],
        repeatLastArgs: 2,
    },
    'COUNTIFS': {
        method: 'countifs',
        parameters: [
            { argumentType: ArgumentTypes.RANGE },
            { argumentType: ArgumentTypes.NOERROR },
        ],
        repeatLastArgs: 2,
    },
};
